import { mustBuildUnitBeforeEndingTurn, type GameState } from "@dailywars/core/engine";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useCallback, useState, type Dispatch, type SetStateAction } from "react";

import { Button } from "#/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import { useHasUnactedOwnUnits } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";

type EndTurnDialogKind = "unacted" | "must-build";

function EndTurnConfirmDialog({
  open,
  onDismiss,
  onConfirm,
  pending,
}: {
  open: boolean;
  onDismiss: () => void;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <DialogContent showCloseButton={false} initialFocus={false}>
        <DialogHeader>
          <DialogTitle>End turn?</DialogTitle>
          <DialogDescription>You still have units that can move or attack.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDismiss}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={() => void onConfirm()}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EndTurnMustBuildDialog({ open, onDismiss }: { open: boolean; onDismiss: () => void }) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <DialogContent showCloseButton={false} initialFocus={false}>
        <DialogHeader>
          <DialogTitle>You must build something</DialogTitle>
          <DialogDescription>
            Please build at least one unit before ending your turn.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={onDismiss}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EndTurnDialogs({
  dialogKind,
  onDismiss,
  onConfirm,
  pending,
}: {
  dialogKind: EndTurnDialogKind | null;
  onDismiss: () => void;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  return (
    <>
      <EndTurnConfirmDialog
        open={dialogKind === "unacted"}
        onDismiss={onDismiss}
        onConfirm={onConfirm}
        pending={pending}
      />
      <EndTurnMustBuildDialog open={dialogKind === "must-build"} onDismiss={onDismiss} />
    </>
  );
}

function requestEndTurnAction({
  localState,
  confirmBeforeEndTurn,
  hasUnactedOwnUnits,
  showUnmovedHighlight,
  setDialogKind,
  onConfirm,
}: {
  localState: GameState;
  confirmBeforeEndTurn: boolean;
  hasUnactedOwnUnits: boolean;
  showUnmovedHighlight: () => void;
  setDialogKind: (kind: EndTurnDialogKind) => void;
  onConfirm: () => void | Promise<void>;
}) {
  if (mustBuildUnitBeforeEndingTurn(localState)) {
    setDialogKind("must-build");
    return;
  }
  if (confirmBeforeEndTurn && hasUnactedOwnUnits) {
    showUnmovedHighlight();
    setDialogKind("unacted");
    return;
  }
  void onConfirm();
}

function useEndTurnDialogControls({
  dialogKind,
  setDialogKind,
  onConfirm,
  pending,
}: {
  dialogKind: EndTurnDialogKind | null;
  setDialogKind: Dispatch<SetStateAction<EndTurnDialogKind | null>>;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  const startUnmovedHighlightFade = useGameSelector((s) => s.startUnmovedHighlightFade);
  const clearUnmovedHighlight = useGameSelector((s) => s.clearUnmovedHighlight);

  const dismiss = useCallback(() => {
    if (dialogKind === "unacted") startUnmovedHighlightFade();
    setDialogKind(null);
  }, [dialogKind, setDialogKind, startUnmovedHighlightFade]);

  const confirmAndClose = useCallback(async () => {
    clearUnmovedHighlight();
    setDialogKind(null);
    await onConfirm();
  }, [clearUnmovedHighlight, onConfirm, setDialogKind]);

  useHotkey(
    "Space",
    (event) => {
      if (!dialogKind || pending) return;
      event.preventDefault();
      if (dialogKind === "must-build") dismiss();
      else void confirmAndClose();
    },
    { enabled: dialogKind !== null, ignoreInputs: false, conflictBehavior: "allow" },
  );

  return { dismiss, confirmAndClose };
}

export function useEndTurnConfirmation({
  onConfirm,
  pending,
  confirmBeforeEndTurn = true,
}: {
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
  confirmBeforeEndTurn?: boolean;
}) {
  const [dialogKind, setDialogKind] = useState<EndTurnDialogKind | null>(null);
  const localState = useGameSelector((s) => s.localState);
  const hasUnactedOwnUnits = useHasUnactedOwnUnits();
  const showUnmovedHighlight = useGameSelector((s) => s.showUnmovedHighlight);
  const { dismiss, confirmAndClose } = useEndTurnDialogControls({
    dialogKind,
    setDialogKind,
    onConfirm,
    pending,
  });

  const requestEndTurn = useCallback(() => {
    requestEndTurnAction({
      localState,
      confirmBeforeEndTurn,
      hasUnactedOwnUnits,
      showUnmovedHighlight,
      setDialogKind,
      onConfirm,
    });
  }, [confirmBeforeEndTurn, hasUnactedOwnUnits, localState, onConfirm, showUnmovedHighlight]);

  return {
    requestEndTurn,
    confirmDialog: (
      <EndTurnDialogs
        dialogKind={dialogKind}
        onDismiss={dismiss}
        onConfirm={confirmAndClose}
        pending={pending}
      />
    ),
    dialogOpen: dialogKind !== null,
  };
}
